"use client";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { Icon, type PhosphorIconName } from "../Icon";

export type NoticeVariant = "success" | "error" | "warning" | "info";

export interface NoticeProps {
  /** Unique identifier for the notice */
  id: string;
  /** The message to display */
  message: string;
  /** Visual variant of the notice */
  variant?: NoticeVariant;
  /** Duration in milliseconds before auto-dismissal (0 = no auto-dismiss) */
  duration?: number;
  /** Whether the notice can be dismissed */
  dismissible?: boolean;
  /** Callback when the notice is dismissed */
  onDismiss?: () => void;
  /** Additional CSS classes */
  className?: string;
}

const baseContainerStyles =
  "bg-background-surface border-l-[3px] border-y border-r border-border-default text-text-primary";

const variantStyles = {
  success: {
    container: baseContainerStyles,
    iconName: "CheckCircle" as PhosphorIconName,
    iconColor: "text-feedback-success-text",
    borderColor: "var(--color-feedback-success-border)",
  },
  error: {
    container: baseContainerStyles,
    iconName: "XCircle" as PhosphorIconName,
    iconColor: "text-feedback-error-text",
    borderColor: "var(--color-feedback-error-border)",
  },
  warning: {
    container: baseContainerStyles,
    iconName: "Warning" as PhosphorIconName,
    iconColor: "text-feedback-warning-text",
    borderColor: "var(--color-feedback-warning-border)",
  },
  info: {
    container: baseContainerStyles,
    iconName: "Info" as PhosphorIconName,
    iconColor: "text-feedback-info-text",
    borderColor: "var(--color-feedback-info-border)",
  },
};

/**
 * Notice
 *
 * Individual notification component that displays a message with an icon.
 * Typically used within a NoticeProvider for managing multiple notifications.
 */
export function Notice({
  id: _id,
  message,
  variant = "info",
  duration = 5000,
  dismissible = true,
  onDismiss,
  className,
}: NoticeProps) {
  const { container, iconName, iconColor, borderColor } = variantStyles[variant];

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onDismiss?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
      className={twMerge("pointer-events-auto flex items-center gap-3 rounded-lg p-4 shadow-lg", container, className)}
      style={{ borderLeftColor: borderColor }}
    >
      <Icon name={iconName} size={20} className={iconColor} />
      <p className="flex-1 select-text text-sm font-medium">{message}</p>
      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          className="flex-shrink-0 rounded-md p-1 transition-colors hover:bg-background-hover"
          aria-label="Dismiss notification"
        >
          <Icon name="X" size={16} />
        </button>
      )}
    </motion.div>
  );
}
